<template>
    <div v-if="pageData.data" class="disaster-dollar-database">
        <div class="hero">
            <figure class="is-tall image-hero">
                <img src="https://carnegie-production-assets.s3.amazonaws.com/static/media/images/hurricane-disaster-united-states-iStock-1869990999.jpg" alt="Hurricane from space" class="image">
            </figure>
        </div>
        <MainWithAsidesTier>
            <template #main-left>
            </template>
            <template #main-center>
                <slot name="main-center">
                    <h2>{{ pageData.data.title }}</h2>
                    <RichTextElements 
                        :elements="pageData.data.dek" 
                        bodyStyle="sans-large"
                        pageContext="publication"
                        class="dek" 
                    />
                    <div class="rich-text">
                        <p>In the United States, disaster recovery is a three-legged stool: government funding sits alongside insurance and private funds (including commercial loans, as well as charitable donations). There is no comprehensive place where disaster survivors, policymakers, and the media can see the different kinds of federal assistance available for response to individual disasters. This database does not encompass the full range of federal grantmaking, but it makes visible the major sources of grant-based funding to individuals and communities through the Federal Emergency Management Agency (FEMA) and the Department of Housing and Urban Development (HUD).</p>
                        <p>The database includes disasters for which FEMA has activated either its Public Assistance or Individuals and Households Program and identifies HUD’s Community Development Block Grant-Disaster Recovery grants associated with these disasters, if any. It currently goes back to 2003. </p>
                        <p>Please reach out to <a href="mailto:pressoffice@ceip.org">pressoffice@ceip.org</a> for media inquiries about the database. </p>
                    </div>
                    
                </slot>
            </template>
            <template #main-right>
                <div class="person-list-item" size="33">
                    <div class="person-titles">
                        <a href="/people/sarah-labowitz?lang=en" class="anchor">
                            <p class="body-display-small direction-ltr typography title">Sarah Labowitz</p>
                        </a>
                        <p class="body-display-micro-italic direction-ltr typography person-title">Nonresident Scholar, Sustainability, Climate, and Geopolitics Program</p>
                    </div>
                    <div class="size-default avatar" path="/people/sarah-labowitz?lang=en">
                        <span class="avatar">
                            <img src="https://carnegie-production-assets.s3.amazonaws.com/static/media/avatars/SL-headshot_02-bw__1_-1.jpg" width="127" height="162" srcset="" sizes="" class="image desaturate" id="6574a0dc0f74e6e8e2b7fde6" mimetype="image/jpeg" __typename="Avatar">
                        </span>
                    </div>
                </div>
                <div class="about-button">
                    <a class="button teal overlay-trigger" href="#my-overlay-content">Methodology</a>
                    <div class="overlay-content" id="my-overlay-content">
                        <div class="banded padded"> 
                            <h3>Data Types</h3>
                            <p>The Disaster Dollar Database tracks federal funding from three sources: FEMA’s Individuals and Households Program (IHP), FEMA’s Public Assistance (PA) program, and the Department of Housing and Urban Development’s (HUD) Community Development Block Grant–Disaster Recovery (CDBG-DR) program. FEMA IHP makes grants to individual households; FEMA PA and CDBG-DR provide funding to government entities. The database tracks funding at the incident level.</p>
                            <h3>Updates</h3>
                            <p>The database is updated approximately twice a year, and whenever Congress appropriates money to HUD and HUD publishes its allocations to jurisdictions in the Federal Register. </p>
                            <h3>Data Sources</h3>
                            <p>FEMA data is gathered from OpenFEMA Data Sets. In some instances, the numbers in the database may differ from those posted in the “Funding Obligations” section of individual disaster declaration webpages hosted by FEMA, which are updated on an ongoing basis. Disaster declaration webpages are linked for each incident. </p>
                            <p>HUD allocations are gathered from the Federal Register. The relevant Federal Register notices are linked throughout the database. </p>
                            <p>Since 2010, HUD has required all grantees to host a dedicated website for CDBG-DR grants, which include documents such as action plans, budgets, and other reports that indicate how CDBG-DR funds are being used. Links to these websites are included when available.  </p>
                            <h3>Data Notes</h3>
                            <p>When a single HUD allocation covers multiple incidents, the value is split evenly among the relevant incidents. When an allocation is divided, the value appears in italics in the database. Refer to the linked Federal Register notice to view the original allocation amount. </p>
                            <p>Federal Register Vol. 69 No. 237 allocates $2,410,256 to the State of West Virginia for incidents 1496, 1500, 1522, 1536, and 1538. DR-1538 is a Pennsylvania incident funded in the same notice in the allocation to the State of Pennsylvania. The State of West Virginia funding is divided among the other four incidents. </p>
                            <p>Federal Register Vol. 78 No. 103 allocates $17,932,000 to the State of Vermont for incidents 1995, 4001, 1022, 4043, and 4066. Incident 1022 is an error and is not included in the database; the funding is divided among the four other incidents. </p>
                            <p>Federal Register Vol. 81, No. 224 and Vol. 84, No. 169 each contain an error related to DR-4272 (Texas Severe Storms and Flooding). This incident is incorrectly grouped with Louisiana incidents in both notices. DR-4272 is listed as a Texas incident in the database.</p>
                            <p>Federal Register Vol. 88, No. 96 allocates $7,473,000 for DR-4657 (Oklahoma Severe Storms, Tornadoes, and Flooding) together with DR-4670 (Muscogee Creek Nation Severe Storms, Tornadoes, and Flooding), which does not appear in the database because there are no FEMA funding obligations for DR-4670. The total allocation amount in the database is assigned to DR-4657.</p>
                            <h3>Version History</h3>
                            <p>February 2025: The database was updated and expanded. It includes 727 incidents dating from 2003 through January 2025. The database was expanded to include all incidents for which FEMA activated either the Individuals and Households Program or the Public Assistance program. FEMA data is now exclusively drawn from OpenFEMA. The database was updated to include HUD allocations from the January 16, 2025 Federal Register Vol. 90, No. 10, as well as twelve Federal Register notices dating from 2004 to 2013. The database has a dedicated website with visualizations to assist users in interpreting the data. </p>
                            <p>August 2024: The database was originally published with 170 incidents dating from 2015 through May 2024. It included incidents for which FEMA activated its Individuals and Households Program. Data from FEMA was drawn from multiple FOIA requests.</p>
                            <h3>Thanks</h3>
                            <p>The database was originally published with research support from 2023-24 Junior Fellows Emily Hardy and Dan Helmeci. 2024-25 Junior Fellow Debbra Goh supported the major expansion of the database published in February 2025. Evan O’Neil designed the visualizations.</p>
                            <p>Amanda Devecka-Rinear of the New Jersey Organizing Project, Chrishelle Palay of the Houston Organizing Movement for Equity, and Maddie Sloan of Texas Appleseed provided early input on the database and how to make it useful for disaster survivors.  </p>

                        </div>
                    </div>
                </div>
                <div class="about-button">
                    <a class="button teal" href="https://carnegie-production-assets.s3.us-east-1.amazonaws.com/static/files/disaster_dollar_database.zip" target="_blank">Download the Data (.zip)</a>
                </div>
            </template>
        </MainWithAsidesTier>
        <div class="section boxes"> 
            <div class="foreground">
                <h3 class="title">Types of Federal Grants </h3>
                <div class="cols cols--equal-heights">
                    <div class="col col-33 pillar-box">
                        <div class="gutters">
                            <h3>FEMA</h3>
                            <p>Individuals and Households Program</p>
                            <Button url="https://www.fema.gov/assistance/individual/program" variant="tertiary" class="cta">Read More</Button>
                        </div>
                    </div>
                    <div class="col col-33 pillar-box">
                        <div class="gutters">
                            <h3>FEMA</h3>
                            <p>Public Assistance</p>
                            <Button url="https://www.fema.gov/assistance/public" variant="tertiary" class="cta">Read More</Button>
                        </div>
                    </div>
                    <div class="col col-33 pillar-box">
                        <div class="gutters">
                            <h3>HUD</h3>
                            <p>Community Development Block Grant-Disaster Recovery </p>
                            <Button url="https://www.hud.gov/program_offices/comm_planning/cdbg-dr" variant="tertiary" class="cta">Read More</Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="data-embeds section">
            <div class="foreground">

                <div class="flourish-embed flourish-chart" data-src="visualisation/21453273"><noscript><img src="https://public.flourish.studio/visualisation/21453273/thumbnail" width="100%" alt="chart visualization" /></noscript></div>
                <div class="flourish-embed flourish-chart" data-src="visualisation/21453283"><noscript><img src="https://public.flourish.studio/visualisation/21453283/thumbnail" width="100%" alt="chart visualization" /></noscript></div>
            </div>
        </div>
        <ClientOnly>
            <SubscribeModal
                :isModalOpen="isSubscribeModalOpen"
                :closeModal="closeSubscribeModal"
                :newsletterFormName="newsletterFormName"
                :scrollOut=true
                />
        </ClientOnly>
        <div class="section download-buttons">
            <div class="foreground">
                <a class="button" href="https://carnegie-production-assets.s3.us-east-1.amazonaws.com/static/files/disaster_dollar_database.zip" target="_blank">Download the Data (.zip)</a>
            </div>
        </div>
        <StreamTierFeed :streamTiered="pageData.data.streamTiered" />
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';
const { scrollProgress } = useScrollProgress();

const pageData = await usePageData(FeatureFragment, 'features');

function revealOverlay(contentWrapperId) {
    document.querySelector(contentWrapperId).classList.add('active');
    const closeButton =
        '<div class="overlay-close"><svg data-v-459a476e="" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="none" class=""><circle cx="18" cy="18" r="17.5" stroke="currentColor"></circle><path stroke="currentColor" d="M25.07 10.93 10.93 25.07M25.07 25.07 10.93 10.93"></path></svg></div>';
    document.querySelector(contentWrapperId).firstElementChild.innerHTML += closeButton;
}

const subscribeCarnegieCookie = useCookie('subscribeCarnegie', {maxAge: 432000})
let modalShown = false;
let subscribe = subscribeCarnegieCookie?.value;
const newsletterFormName = `CarnegieSustainability,Climate,andGeopoliticsProgramForm`;
const isSubscribeModalOpen = ref(false);

const closeSubscribeModal = () => {
    isSubscribeModalOpen.value = false;
};

const handleSubscribeCtaClicked = () => {
    isSubscribeModalOpen.value = true;
};

watch(scrollProgress, () => {
    if (!modalShown && subscribe !== true && scrollProgress.value !== 'undefined' && scrollProgress.value > 5) {
        handleSubscribeCtaClicked();
        subscribeCarnegieCookie.value = true;
        modalShown = true;
    }
});

onMounted(() => {
    const script = document.createElement("script");
    script.src = "https://public.flourish.studio/resources/embed.js";
    document.querySelector('.flourish-embed').appendChild(script);
    // I need to catch any time an existing accordion or overlay is clicked, so it looks for all clicks on anything and acts if the className includes sortable ....
    document.onclick = function (e) {
        if (e.target.classList && e.target.classList.contains('overlay-trigger')) {
            e.preventDefault();
            revealOverlay(e.target.hash);
        }
        if (e.target.classList && e.target.classList.contains('overlay-close')) {
            e.preventDefault();
            document.querySelector('.overlay-content').classList.remove('active');
        }
    };
});

</script>

<style lang="scss" scoped>
/* layout */
$gutter: 30px;
$site-width: 1000px;

$carnegie-blue: #003a63;
$gray-light: #e4e3dd;
$black: #000;
$white: #fff;
$teal: #00a79d;
$dark-blue: #00365e;
$light-blue: #27aae1;
$dark-gray-new: #6e6e71;
$medium-gray-new: #a7a9ac;
$light-gray: #f5f5f5;
$dark-gray: #929497;
$yellow: #fbb040;
$light-blue-light: #a0d9f2;
$medium-blue: #3183b1;
$copper: #89684f;

//-overlay
:deep(.ceip-overlay),
:deep(.overlay-content) {
    display: none;
}

:deep(.overlay-content) {
    position: fixed;
    background: lightgray;
    background: rgba(lightgray, 0.8);
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10000;
    display: none;

    &.active {
        display: block;
    }

    > div {
        background: white;
        border-radius: 7px;
        color: #000000;
        display: block;
        margin: 0 auto;
        position: relative;
        top: 20%;
        width: 60rem;
        max-width: 60rem;
        max-height: 70vh;
        padding: 4rem 2rem;
        overflow: scroll;
        .overlay-close {
            position: absolute;
            top: 1rem;
            right: 1rem;
            svg {
                pointer-events: none;
            }
        }
        @include media-query(mobile) {
            max-width: 90vw;
        }
    }
}

/*layout basics*/
.foreground {
    position: relative;
    margin: 0 auto;
    box-sizing: content-box;
    max-width: $site-width;
    padding-left: calc($gutter / 2);
    padding-right: calc($gutter / 2);
}

.section {
    display: block;
    clear: both;
    margin-top: $gutter;
    margin-bottom: $gutter;
}

:deep(.cols) {
    clear: both;
    /*use a .no-gutters class to undo these default styles */
    margin-left: calc($gutter / 2) * -1;
    margin-right: calc($gutter / 2) * -1;

    &:before,
    &:after {
        content: ' ';
        display: table;
        height: 0;
        clear: both;
        font-size: 1px;
        line-height: 0px;
        visibility: hidden;
    }

    @include media-query(phone) { 
        margin-left: 0 !important;
        margin-right: 0 !important;
    }
}

.col, :deep(.col) {
    float: left;

    @include media-query(phone) {
         float: none;
         width: 100% !important;
         
         > .gutters {
            margin-left: 0;
            margin-right: 0;
        }
    }
}
:deep(.cols--equal-heights) {
    flex-wrap: wrap;

    &,
    >.col {
        display: flex;

        @include media-query(phone) {
            display: block;
        }
    }

    >.col {
        flex-direction: column;
    }

    .banded {
        flex: 1 0 auto;
    }
}

:deep(.col-33) {
    float: left;
    width: 33%;
}

.padded {
    padding: $gutter;
}

.gutters, :deep(.gutters) {
    margin-left: 15px;
    margin-right: 15px;
}

.disaster-dollar-database {

    .hero {
        margin-bottom: 6em;
        .image {
            height: 60rem;
            -o-object-fit: cover;
            object-fit: cover;
            width: 100%;
        }
    }

    .dek {
        border-bottom: 1px solid var(--color-divider);
        margin-bottom: 2.4rem;
        padding-bottom: 2.4rem;
    }

    .rich-text {
        @include type-body-display-large;
        p {margin-bottom: 1.8rem;}
        a {
            overflow-wrap: break-word;
            text-decoration-color: var(--color-anchor-underline);
            text-decoration-line: underline;
            text-decoration-thickness: .0833333333em;
            -webkit-text-decoration-skip: ink;
            text-decoration-skip-ink: auto;
            text-underline-offset: .25em;
        }
    }
    .person-list-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding-bottom: 6em;
        .author:not(:first-child) & {
            margin-top: 2rem;
        }
        .speaker:not(:first-child) & {
            margin-top: 2rem;
        }
    }

    .title {
        color: palette(text);
        a & {
            color: rgb(var(--color-rgb-blue-default));
        }
    }
    .body-display-small {
        font-family: GT Sectra,Georgia,serif;
        font-size: 1.6rem;
        font-weight: 350;
        line-height: 1.5;
    }
    .body-display-micro-italic {
        font-family: GT Sectra,Georgia,serif;
        font-size: 1.2rem;
        font-style: italic;
        font-weight: 350;
        line-height: 1.1666666667;
    }

    .expert {
        .title {
            color: palette(text-blue);
        }
    }

    .person-title,
    .person-titles .body-style-body-display-micro-italic {
        opacity: 0.6;
    }

    .avatar {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 100rem;
        overflow: hidden;
        aspect-ratio: 1/1;
        color: color(blanc, default, 0.7);
        width: 5.6rem;

        img {
            object-fit: cover;
            width: 100%;
            height: 100%;
        }

        .avatar-link {
            width: 100%;
            height: 100%;
        }
    }

    .about-button {
        margin: 1em 0 2.5em 0;
    }
    .button.teal {
        background-color: $teal;
        border-radius: 10px;
        color: $white;
        font-size: 1.2em;
        margin-left: 25%;
        padding: .5em;
    }

    .main-with-asides-tier {margin-bottom: 5em;}

    .boxes {
        margin-bottom: 10em;
        margin-top: 0;
        h3.title {margin-bottom: 2em; color: $carnegie-blue}
        .cols {margin-bottom: calc($gutter * -2);}
        .col-33 {
            background-color: rgba($teal, .2);
            padding: 3px;
            margin-right: 5%;
            width: 30%;
            &:last-of-type {margin-right: 0;}
            .gutters {padding: 3em 0 1em 0;}
            h3 {
                color: $teal;
                font-family: "Apercu", Helvetica Neue, Helvetica, Arial, sans-serif;
                font-size: 2.5em;
                line-height: 1.5em;
            }
            p {
                letter-spacing: -0.02em;
                font-family: "Apercu", Helvetica Neue, Helvetica, Arial, sans-serif;
                font-weight: 500;
                font-size: 2.8rem;
                line-height: 1.0714285714;
                font-size: 1.6em;
                line-height: 1.2em;
                margin-bottom: 2em;
            }
            a.anchor {
                position: absolute;
                bottom: 30px;
            }
        }
    }

    .download-buttons {
        margin: 5rem 0 15rem 0;

        .foreground {
            text-align: center;
        }

        .button {
            background-color: $teal;
            color: $white;
            cursor: pointer;
            font-size: 1.5rem;
            margin: 0 calc($gutter / 2);
            padding: calc($gutter / 2);
            text-transform: uppercase;

            &:after {
                content: none;
            }

            &:hover {
                background-color: rgba($teal, 0.8);
            }
        }
    }

    .overlay-content div {
        max-height: 80vh;
        width: 570px;
        max-width: 570px;
        top: 30%;
        transform: translateY(-30%);
        overflow-y: scroll;
        h4 {margin-bottom: .5em}
        p {
            margin-bottom: 1em;
            @include type-body;
            & {
                font-weight: 100;
            }
            em {
                font-family: GT Sectra,Georgia,serif;
                font-size: 1.2rem;
                font-style: italic;
                font-weight: 350;
                line-height: 1.1666666667;
            }
            strong {font-family: "Apercu", Helvetica Neue, Helvetica, Arial, sans-serif;}
        }
    }

    @include media-query(tablet) {
        .zone-title {
            .container-title {
                margin-top: 40px;
                h2 {font-size: 2.5rem;}
                .foreground {
                    margin-left: 15px;
                    margin-right: 15px;
                }
            }
        }
        .boxes .foreground, .countries .foreground {
            margin-left: 15px;
            margin-right: 15px;
        }

        .description-section .foreground {
            margin-left: 15px;
            margin-right: 15px;
        }

        .person-list-item {margin-left: 2em;}
    }

    @include media-query(mobile) {

        .hero .image {
            height: 40rem;
        }

        .boxes .col-33 {
            margin-bottom: 2em;

            a.anchor {position: relative}
        }

        .overlay-content div {
            width: 270px;
            max-width: 270px;
        }

        .reponsive-mobile-hide {
            display: none;
        }

        .download-buttons {
            background-color: $white;
            margin: 0;
            padding: $gutter 0;
            position: relative;
            .button:first-child {
                margin-bottom: calc($gutter / 2);
            }
        }

        .ceip-overlay__inner {
            margin-left: 5%;
            max-height: 95%;
            max-width: 90%;
            top: 30%;
        }
    }
}



</style>